import { Subtask } from '@/api/pathways/subtasks/types'
import {
  Communication,
  SubCommunicationType,
} from '@/legacy/types/communications/communications'
import { apiRequest } from '@/libs/api/api'

/**
 * Helper function to determine if a communication is an active planned call.
 * - Has subCommunicationType === PLANNED_CALL
 * - Has no completedDatetime
 * - Has no callDisposition
 * @param comm - The communication to check
 * @returns true if the communication is an active planned call
 */
export function isActivePlannedCall(
  comm: Communication | null | undefined
): boolean {
  if (!comm) return false
  return (
    comm.subCommunicationType === SubCommunicationType.PLANNED_CALL &&
    !comm.completedDatetime &&
    !comm.callDisposition
  )
}

/**
 * Verifies that patient IDs match between communication and subtask.
 * @param comm - The communication to check
 * @param subtask - The subtask to verify against
 * @returns true if patient IDs match or if communication has no patient ID
 */
function hasMatchingPatientId(comm: Communication, subtask: Subtask): boolean {
  const commPatientId = comm.patientIds?.[0]
  if (!commPatientId) return true
  return commPatientId === subtask.memberId
}

/**
 * Verifies bidirectional link between communication and subtask.
 * If subtaskMaps is undefined (cached/partial response), returns true for backward compatibility.
 * @param comm - The communication to check
 * @param subtask - The subtask to verify against
 * @returns true if bidirectional link is valid or subtaskMaps unavailable
 */
function hasBidirectionalLink(comm: Communication, subtask: Subtask): boolean {
  if (comm.subtaskMaps === undefined) return true
  if (!comm.subtaskMaps.length) return false
  return comm.subtaskMaps.some((sm) => sm.subtaskId === subtask.subtaskId)
}

/**
 * Fetches a communication directly.
 * Uses the consolidated endpoint which returns { communication, displayDate }.
 * @param communicationId - The ID of the communication to fetch
 * @returns The communication object, or null if not found
 */
async function fetchCommunication(
  communicationId: string
): Promise<Communication | null> {
  try {
    const response = await apiRequest<{
      communication: Communication
      displayDate: string
    }>('GET', `/api/communications/consolidated/${communicationId}`)

    return response.communication ?? null
  } catch {
    return null
  }
}

/**
 * Checks if a subtask is linked to an active planned call and returns navigation data.
 * Call this at click time (not setup time) to avoid staleness issues.
 * @param subtask - The subtask to check (pass at call time, not capture at setup)
 * @returns Navigation data if linked to an active planned call, null otherwise
 */
export async function getLinkedActivePlannedCall(
  subtask: Subtask
): Promise<{ communicationId: string; patientId: string } | null> {
  if (!subtask.communicationMaps?.length) {
    return null
  }

  // Fetch all linked communications concurrently
  // Using apiRequest directly avoids Pinia singleton race conditions
  const communications = await Promise.all(
    subtask.communicationMaps.map((cm) =>
      fetchCommunication(cm.communicationId)
    )
  )

  // Find the first active planned call that's properly linked
  for (const [i, comm] of communications.entries()) {
    const cm = subtask.communicationMaps[i]

    if (!comm) continue
    if (!isActivePlannedCall(comm)) continue
    if (!hasMatchingPatientId(comm, subtask)) continue
    if (!hasBidirectionalLink(comm, subtask)) continue

    const patientId = comm.patientIds?.[0] ?? subtask.memberId
    return {
      communicationId: cm.communicationId,
      patientId,
    }
  }

  return null
}
